import { cn } from '@/lib/utils';
import { STRINGS } from '@/strings/strings';

type Strength = 'empty' | 'weak' | 'medium' | 'strong';

function countCharClasses(password: string): number {
  let count = 0;
  if (/[a-z]/.test(password)) count++;
  if (/[A-Z]/.test(password)) count++;
  if (/[0-9]/.test(password)) count++;
  if (/[^a-zA-Z0-9]/.test(password)) count++;
  return count;
}

function computeStrength(password: string): Strength {
  if (!password) return 'empty';
  const classes = countCharClasses(password);
  if (password.length < 8 || classes < 2) return 'weak';
  if (password.length >= 12 && classes >= 3) return 'strong';
  return 'medium';
}

interface Props {
  password: string;
}

export function PasswordStrengthMeter({ password }: Props) {
  const strength = computeStrength(password);
  if (strength === 'empty') return null;

  const level = strength === 'weak' ? 1 : strength === 'medium' ? 2 : 3;
  const color =
    strength === 'weak'
      ? 'bg-[color:var(--color-error)]'
      : strength === 'medium'
        ? 'bg-dream-gold'
        : 'bg-[color:var(--color-success)]';
  const label =
    strength === 'weak'
      ? STRINGS.auth.passwordStrength.weak
      : strength === 'medium'
        ? STRINGS.auth.passwordStrength.medium
        : STRINGS.auth.passwordStrength.strong;

  return (
    <div className="mt-1.5" aria-live="polite">
      <div className="flex gap-1">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className={cn('h-1 flex-1 rounded-full', i <= level ? color : 'bg-white/20')}
          />
        ))}
      </div>
      <div className="mt-1 text-xs text-dream-white/70">
        {STRINGS.auth.passwordStrength.label}:{' '}
        <span className="font-medium text-dream-white">{label}</span>
      </div>
    </div>
  );
}
